"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.logQueryIfNeeded = exports.connectorScopeLevels = exports.ConnectorAttributeStats = exports.ConnectorAttributeStatsValue = exports.ConnectorEntityStats = exports.QueryAnalyze = exports.QueryResults = exports.QueryResultsAttribute = exports.DatabaseQuery = exports.QueryId = exports.connectorSchemaOptsDefaults = exports.ConnectorSchemaConfOpts = exports.ConnectorSchemaDataOpts = exports.ConnectorScopeOpts = exports.ConnectorConfOpts = void 0;
exports.formatConnectorScope = formatConnectorScope;
exports.isPolymorphic = isPolymorphic;
exports.queryError = queryError;
exports.handleError = handleError;
exports.buildQueryAttributes = buildQueryAttributes;
const zod_1 = require("zod");
const utils_1 = require("@azimutt/utils");
const common_1 = require("../common");
const database_1 = require("../database");
const sql_1 = require("../helpers/sql");
exports.ConnectorConfOpts = zod_1.z.object({
    logQueries: zod_1.z.boolean().optional() // default: false, log executed queries using the provided logger
}).strict();
exports.ConnectorScopeOpts = zod_1.z.object({
    // filters: limit the scope of the extraction
    database: database_1.DatabaseName.optional(), // export only this database or database pattern (use LIKE if contains %, equality otherwise)
    catalog: database_1.CatalogName.optional(), // export only this catalog or catalog pattern (use LIKE if contains %, equality otherwise)
    schema: database_1.SchemaName.optional(), // export only this schema or schema pattern (use LIKE if contains %, equality otherwise)
    entity: database_1.EntityName.optional(), // export only this entity or entity pattern (use LIKE if contains %, equality otherwise)
}).strict();
exports.ConnectorSchemaDataOpts = zod_1.z.object({
    // data access: get more interesting result, beware of performance
    sampleSize: zod_1.z.number().optional(), // default: 100, number of documents used to infer a schema (document databases, json attributes in relational db...)
    inferMixedJson: zod_1.z.string().optional(), // when inferring JSON, will differentiate documents on this attribute, useful when storing several documents in the same collection in Mongo or Couchbase
    inferJsonAttributes: zod_1.z.boolean().optional(), // will get sample values from JSON attributes to infer a schema (nested attributes)
    inferPolymorphicRelations: zod_1.z.boolean().optional(), // will get distinct values from the kind attribute of polymorphic relations to create relations
    inferRelationsFromJoins: zod_1.z.boolean().optional(), // will fetch historical queries and suggest relations based on joins
    inferPii: zod_1.z.boolean().optional(), // will fetch sample rows from tables to detect columns with Personal Identifiable Information
});
exports.ConnectorSchemaConfOpts = zod_1.z.object({
    // post analysis:
    inferRelations: zod_1.z.boolean().optional(), // default: false, infer relations based on attribute names
    // behavior
    ignoreErrors: zod_1.z.boolean().optional(), // default: false, ignore errors when fetching the schema, just log them
});
exports.connectorSchemaOptsDefaults = {
    sampleSize: 100
};
exports.QueryId = zod_1.z.string();
// TODO define more generic and meaningful structure
exports.DatabaseQuery = zod_1.z.object({
    id: exports.QueryId, // query id to group duplicates
    user: zod_1.z.string().optional(), // the user executing the query
    database: database_1.DatabaseName, // the database in which the query was executed
    query: zod_1.z.string(),
    rows: zod_1.z.number(), // accumulated rows retrieved or affected by the query
    plan: zod_1.z.object({ count: zod_1.z.number(), minTime: common_1.Millis, maxTime: common_1.Millis, sumTime: common_1.Millis, meanTime: common_1.Millis, sdTime: common_1.Millis }).strict().optional(),
    exec: zod_1.z.object({ count: zod_1.z.number(), minTime: common_1.Millis, maxTime: common_1.Millis, sumTime: common_1.Millis, meanTime: common_1.Millis, sdTime: common_1.Millis }).strict().optional(),
    blocks: zod_1.z.object({ sumRead: zod_1.z.number(), sumWrite: zod_1.z.number(), sumHit: zod_1.z.number(), sumDirty: zod_1.z.number() }).optional(), // data from tables & indexes
    blocksTmp: zod_1.z.object({ sumRead: zod_1.z.number(), sumWrite: zod_1.z.number(), sumHit: zod_1.z.number(), sumDirty: zod_1.z.number() }).optional(), // data from temporary tables & indexes
    blocksQuery: zod_1.z.object({ sumRead: zod_1.z.number(), sumWrite: zod_1.z.number() }).optional(), // data used for the query (sorts, hashes...)
}).strict().describe('DatabaseQuery');
exports.QueryResultsAttribute = zod_1.z.object({
    name: zod_1.z.string(),
    ref: database_1.AttributeRef.optional()
}).strict();
exports.QueryResults = zod_1.z.object({
    query: zod_1.z.string(),
    attributes: exports.QueryResultsAttribute.array(),
    rows: common_1.JsValue.array()
}).strict().describe('QueryResults');
// TODO
exports.QueryAnalyze = zod_1.z.object({}).strict().describe('QueryAnalyze');
exports.ConnectorEntityStats = database_1.EntityRef.extend({
    rows: zod_1.z.number(),
    sampleValues: zod_1.z.record(database_1.AttributeValue)
}).strict().describe('ConnectorEntityStats');
exports.ConnectorAttributeStatsValue = zod_1.z.object({
    value: database_1.AttributeValue,
    count: zod_1.z.number()
}).strict();
exports.ConnectorAttributeStats = database_1.AttributeRef.extend({
    type: database_1.AttributeType,
    rows: zod_1.z.number(),
    nulls: zod_1.z.number(),
    cardinality: zod_1.z.number(),
    commonValues: exports.ConnectorAttributeStatsValue.array()
}).strict().describe('ConnectorAttributeStats');
exports.connectorScopeLevels = ['database', 'catalog', 'schema', 'entity'];
function formatConnectorScope(names, opts) {
    const filters = exports.connectorScopeLevels
        .map(key => [names[key], opts[key]])
        .filter((v) => !!v[0] && !!v[1])
        .map(([name, opt]) => `${opt.includes('%') ? (0, utils_1.plural)(name) : name} '${opt}'`);
    if (filters.length > 0) {
        return (0, utils_1.joinLast)(filters, ', ', ' and ');
    }
    else {
        return '';
    }
}
const logQueryIfNeeded = (id, name, sql, parameters, exec, count, { logger, logQueries }) => {
    if (logQueries) {
        const start = Date.now();
        const queryId = `#${id}${name ? ' ' + name : ''}`;
        logger.log(`${queryId} exec:\n${(0, utils_1.indent)((0, utils_1.stripIndent)(sql))}`);
        const res = exec(sql, parameters);
        res.then(r => logger.log(`${queryId} success: ${count(r)} rows in ${Date.now() - start} ms`), e => logger.log(`${queryId} failure: ${e} in ${Date.now() - start} ms`));
        return res;
    }
    else {
        return exec(sql, parameters);
    }
};
exports.logQueryIfNeeded = logQueryIfNeeded;
function isPolymorphic(attr, entityAttrs) {
    return ['type', 'class', 'kind'].some(suffix => {
        if (attr.endsWith(suffix)) {
            const related = attr.slice(0, -suffix.length) + 'id';
            return entityAttrs.some(c => c === related);
        }
        else if (attr.endsWith(suffix.toUpperCase())) {
            const related = attr.slice(0, -suffix.length) + 'ID';
            return entityAttrs.some(c => c === related);
        }
        else if (attr.endsWith(suffix.charAt(0).toUpperCase() + suffix.slice(1))) {
            const related = attr.slice(0, -suffix.length) + 'Id';
            return entityAttrs.some(c => c === related);
        }
        else {
            return false;
        }
    });
}
function queryError(name, sql, err) {
    const formattedSql = (0, utils_1.stripIndent)(sql);
    if (typeof err === 'object' && err !== null) {
        return new Error(err.constructor.name +
            ('code' in err && err.code ? ` ${err.code}` : '') +
            (name ? ` on query ${name}` : '') +
            ('message' in err && err.message ? `: ${err.message}` : '') +
            `\nQuery: ${formattedSql}`);
    }
    else if (err) {
        return new Error(`Error ${JSON.stringify(err)}\n on '${formattedSql}'`);
    }
    else {
        return new Error(`Error on '${formattedSql}'`);
    }
}
function handleError(msg, onError, { logger, ignoreErrors }) {
    return (err) => {
        if (ignoreErrors) {
            logger.warn(`${msg}. Ignoring...`);
            return Promise.resolve(onError);
        }
        else {
            return Promise.reject(err);
        }
    };
}
// assign column ref to query columns by parsing the sql query
function buildQueryAttributes(fields, query) {
    const keys = {};
    const statement = (0, sql_1.parseSqlStatement)(query);
    const wildcardTable = getWildcardSelectTable(statement);
    const allDefinedColumns = getAllDefinedColumns(statement, fields.length);
    return fields.map((field, i) => {
        const name = uniqueName(field.name, keys);
        keys[name] = true;
        const ref = field.table && field.column ? { schema: field.schema, entity: field.table, attribute: [field.column] } :
            wildcardTable ? { ...wildcardTable, attribute: [field.name] } :
                allDefinedColumns ? allDefinedColumns[i] : undefined;
        return (0, utils_1.removeUndefined)({ name, ref });
    });
}
function getWildcardSelectTable(statement) {
    if (statement && statement.command === 'SELECT') {
        if (statement.joins === undefined && statement.columns.length === 1 && statement.columns[0].name === '*') {
            return (0, utils_1.removeUndefined)({ schema: statement.table.schema, entity: statement.table.name });
        }
    }
    return undefined;
}
function getAllDefinedColumns(statement, colCount) {
    if (statement && statement.command === 'SELECT') {
        if (statement.columns.length === colCount && statement.columns.every(c => c.name !== '*')) {
            const mainEntity = (0, utils_1.removeUndefined)({ schema: statement.table.schema, entity: statement.table.name });
            const aliases = (statement.joins || []).reduce((acc, j) => {
                return j.alias ? { ...acc, [j.alias]: (0, utils_1.removeUndefined)({ schema: j.schema, entity: j.table }) } : acc;
            }, statement.table.alias ? { [statement.table.alias]: mainEntity } : {});
            return statement.columns.map(c => {
                return c.col ? { ...c.scope ? aliases[c.scope] : mainEntity, attribute: c.col } : undefined;
            });
        }
    }
    return [];
}
function uniqueName(name, currentNames, cpt = 1) {
    const newName = cpt === 1 ? name : `${name}_${cpt}`;
    if (currentNames[newName]) {
        return uniqueName(name, currentNames, cpt + 1);
    }
    else {
        return newName;
    }
}
//# sourceMappingURL=connector.js.map